/**
 * Social Login Buttons Component for Re:Nav-96
 *
 * Displays buttons for Google, Facebook, and Microsoft OAuth login.
 * Uses the API service to redirect to the appropriate OAuth endpoints.
 */

import { useEffect, useState } from 'react';
import { authApi } from '@/services/api';

/**
 * Props for SocialLoginButtons component
 */
interface SocialLoginButtonsProps {
  /** Whether the buttons should be disabled */
  disabled?: boolean;
}

type ProvidersInfo = {
  available: { google: boolean; facebook: boolean; microsoft: boolean };
};

/**
 * Facebook icon SVG component
 */
function FacebookIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.469H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12z" />
    </svg>
  );
}

/**
 * Google icon SVG component
 */
function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Microsoft icon SVG component
 */
function MicrosoftIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

/**
 * Social login buttons for OAuth providers
 *
 * Renders buttons for Google, Facebook, and Microsoft login.
 * Clicking a button redirects to the OAuth authorization flow.
 *
 * @example
 * <SocialLoginButtons disabled={isLoading} />
 */
export function SocialLoginButtons({ disabled = false }: SocialLoginButtonsProps) {
  const [providers, setProviders] = useState<ProvidersInfo['available'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApi
      .getProviders()
      .then((info) => {
        if (!cancelled) setProviders(info.available);
      })
      .catch(() => {
        if (!cancelled) setProviders({ google: false, facebook: false, microsoft: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showGoogle = providers?.google === true;
  const showFacebook = providers?.facebook === true;
  const showMicrosoft = providers?.microsoft === true;

  if (providers !== null && !showGoogle && !showFacebook && !showMicrosoft) {
    // No social providers configured — render nothing.
    return null;
  }

  /**
   * Handle social login button click — redirect to the appropriate OAuth
   * provider's backend endpoint, which starts the OAuth dance and lands
   * back at /auth/callback with the tokens.
   */
  const handleSocialLogin = (provider: 'google' | 'facebook' | 'microsoft') => {
    const authUrl =
      provider === 'google'
        ? authApi.getGoogleAuthUrl()
        : provider === 'facebook'
        ? authApi.getFacebookAuthUrl()
        : authApi.getMicrosoftAuthUrl();
    window.location.href = authUrl;
  };

  return (
    <div className="space-y-3">
      {showGoogle && (
        <button
          type="button"
          onClick={() => handleSocialLogin('google')}
          disabled={disabled}
          className="flex items-center justify-center gap-3 w-full px-4 py-3
                     bg-white text-gray-800 font-medium rounded-lg
                     transition-all duration-200
                     hover:bg-gray-100 hover:shadow-md
                     focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-secondary
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <GoogleIcon />
          <span>Continue with Google</span>
        </button>
      )}

      {showFacebook && (
        <button
          type="button"
          onClick={() => handleSocialLogin('facebook')}
          disabled={disabled}
          className="flex items-center justify-center gap-3 w-full px-4 py-3
                     bg-[#1877F2] text-white font-medium rounded-lg
                     transition-all duration-200
                     hover:bg-[#166fe5] hover:shadow-md
                     focus:outline-none focus:ring-2 focus:ring-[#1877F2] focus:ring-offset-2 focus:ring-offset-secondary
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FacebookIcon />
          <span>Continue with Facebook</span>
        </button>
      )}

      {showMicrosoft && (
        <button
          type="button"
          onClick={() => handleSocialLogin('microsoft')}
          disabled={disabled}
          className="flex items-center justify-center gap-3 w-full px-4 py-3
                     bg-secondary-light text-white font-medium rounded-lg
                     border border-white/20
                     transition-all duration-200
                     hover:bg-secondary-light/80 hover:shadow-md
                     focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-secondary
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <MicrosoftIcon />
          <span>Continue with Microsoft</span>
        </button>
      )}
    </div>
  );
}

export default SocialLoginButtons;
